
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Calculator, Scale, Target, Clock } from 'lucide-react';
import { 
  calculateBMIResults, 
  convertFeetInchesToCm, 
  convertLbsToKg,
  convertKgToLbs,
  BMIData,
  BMIResults 
} from '@/utils/bmiCalculations';

export const BMICalculator: React.FC = () => {
  const [units, setUnits] = useState<'metric' | 'imperial'>('imperial');
  const [feet, setFeet] = useState<string>('');
  const [inches, setInches] = useState<string>('');
  const [heightCm, setHeightCm] = useState<string>('');
  const [weightLbs, setWeightLbs] = useState<string>('');
  const [weightKg, setWeightKg] = useState<string>('');
  const [age, setAge] = useState<string>('');
  const [gender, setGender] = useState<'male' | 'female'>('female');
  const [activityLevel, setActivityLevel] = useState<string>('sedentary');
  const [results, setResults] = useState<BMIResults | null>(null);

  const calculateResults = () => {
    try {
      let heightInCm: number;
      let weightInKg: number;

      if (units === 'imperial') {
        if (!feet || !inches || !weightLbs) return;
        heightInCm = convertFeetInchesToCm(parseInt(feet), parseInt(inches));
        weightInKg = convertLbsToKg(parseFloat(weightLbs));
      } else {
        if (!heightCm || !weightKg) return;
        heightInCm = parseFloat(heightCm);
        weightInKg = parseFloat(weightKg);
      }

      if (!age) return;

      const data: BMIData = {
        height: heightInCm,
        weight: weightInKg,
        age: parseInt(age),
        gender,
        activityLevel: activityLevel as any
      };

      const calculatedResults = calculateBMIResults(data);
      setResults(calculatedResults);
    } catch (error) {
      console.error('Error calculating BMI:', error);
    }
  };

  useEffect(() => {
    calculateResults();
  }, [feet, inches, heightCm, weightLbs, weightKg, age, gender, activityLevel, units]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-fasttrack-blue" />
            BMI Calculator & Fat Loss Estimator
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Units Toggle */}
          <div className="flex gap-2">
            <Button
              variant={units === 'imperial' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setUnits('imperial')}
            >
              Imperial
            </Button>
            <Button
              variant={units === 'metric' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setUnits('metric')}
            >
              Metric
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Height Input */}
            <div className="space-y-2">
              <Label>Height</Label>
              {units === 'imperial' ? (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input
                      placeholder="Feet"
                      value={feet}
                      onChange={(e) => setFeet(e.target.value)}
                      type="number"
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      placeholder="Inches"
                      value={inches}
                      onChange={(e) => setInches(e.target.value)}
                      type="number"
                    />
                  </div>
                </div>
              ) : (
                <Input
                  placeholder="Height (cm)"
                  value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)}
                  type="number"
                />
              )}
            </div>

            {/* Weight Input */}
            <div className="space-y-2">
              <Label>Weight</Label>
              {units === 'imperial' ? (
                <Input
                  placeholder="Weight (lbs)"
                  value={weightLbs}
                  onChange={(e) => setWeightLbs(e.target.value)}
                  type="number"
                />
              ) : (
                <Input
                  placeholder="Weight (kg)"
                  value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)}
                  type="number"
                />
              )}
            </div>

            {/* Age */}
            <div className="space-y-2">
              <Label>Age</Label>
              <Input
                placeholder="Age (years)"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                type="number"
              />
            </div>

            {/* Gender */}
            <div className="space-y-2">
              <Label>Gender</Label>
              <Select value={gender} onValueChange={(value: 'male' | 'female') => setGender(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Activity Level */}
            <div className="space-y-2 md:col-span-2">
              <Label>Activity Level</Label>
              <Select value={activityLevel} onValueChange={setActivityLevel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sedentary">Sedentary (little/no exercise)</SelectItem>
                  <SelectItem value="light">Light (light exercise 1-3 days/week)</SelectItem>
                  <SelectItem value="moderate">Moderate (moderate exercise 3-5 days/week)</SelectItem>
                  <SelectItem value="active">Active (hard exercise 6-7 days/week)</SelectItem>
                  <SelectItem value="very-active">Very Active (very hard exercise, physical job)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* BMI Results */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Scale className="h-5 w-5" />
                Your BMI Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-center">
                <div className="text-3xl font-bold text-fasttrack-blue">
                  {results.bmi.toFixed(1)}
                </div>
                <div className={`text-lg font-medium ${results.categoryColor}`}>
                  {results.category}
                </div>
              </div>
              
              <div className="text-sm text-muted-foreground">
                <p>Healthy BMI range: 18.5 - 24.9</p>
                <p>
                  Healthy weight range: {' '}
                  {units === 'imperial' 
                    ? `${convertKgToLbs(results.healthyWeightRange.min).toFixed(0)} - ${convertKgToLbs(results.healthyWeightRange.max).toFixed(0)} lbs`
                    : `${results.healthyWeightRange.min.toFixed(0)} - ${results.healthyWeightRange.max.toFixed(0)} kg`
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Fat Loss Estimation */}
          {results.fatToLose > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Target className="h-5 w-5" />
                  Fat Loss Goals
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Target weight:</span>
                    <span className="font-medium">
                      {units === 'imperial' 
                        ? `${convertKgToLbs(results.targetWeight).toFixed(0)} lbs`
                        : `${results.targetWeight.toFixed(0)} kg`
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Fat to lose:</span>
                    <span className="font-medium text-orange-600">
                      {units === 'imperial' 
                        ? `${convertKgToLbs(results.fatToLose).toFixed(0)} lbs`
                        : `${results.fatToLose.toFixed(0)} kg`
                      }
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Calories to burn:</span>
                    <span className="font-medium text-red-600">
                      {Math.round(results.caloriesToBurn).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Your BMR:</span>
                    <span className="font-medium">
                      {Math.round(results.bmr)} cal/day
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Fasting Duration Estimates */}
      {results && results.fatToLose > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Fasting Duration Estimates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-muted-foreground mb-4">
              <p>Estimated time to reach healthy BMI through different fasting protocols:</p>
              <p className="text-xs mt-1">*These are estimates based on BMR and typical fasting metabolic effects. Individual results may vary.</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {results.fastingEstimates.map((estimate, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <div>
                    <div className="font-medium text-sm">{estimate.protocol}</div>
                    <div className="text-xs text-muted-foreground">
                      {estimate.hoursPerDay}h fasting daily
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-fasttrack-blue">
                      {estimate.weeksToGoal} weeks
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ({estimate.daysToGoal} days)
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <p className="text-xs text-yellow-800 dark:text-yellow-200">
                <strong>Disclaimer:</strong> This calculator provides estimates only. Please consult with a healthcare provider before starting any fasting regimen, especially if you have health conditions, are pregnant, breastfeeding, or taking medications.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
